import { useState } from "react";
import { MoreHoriz, NavArrowRight } from "iconoir-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
    DropdownMenuSeparator,
    dropdownMenuItemDestructiveClass,
} from "components/ui/dropdown-menu";
import { ConversationMenuIcon } from "components/blocks/layout/conversation-menu-icon";
import { cn } from "lib/utils";
import { CustomDialog } from "components/blocks/custom-dialog";
import { DebouncedInput } from "components/blocks/debounced-input/debounced-input";
import { useToast } from "../../../hooks/use-toast";
import { deleteConversationById } from "../../../services/storage-service";
import { useConfig } from "../../../hooks/use-config";
import { useChatStore } from "../../../state/store/chat/use-chat-store";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useUpdateProject } from "../hooks/use-create-project";
import { SidebarItem } from "/components/blocks/layout/types/sidebar-conversation";
import { useQueryClient } from "@tanstack/react-query";
import { markConversationDeletedLocally, clearLocallyDeletedConversation } from "features/chat/utils/agent-sidebar-sync";
import { NewProjectModal } from "./new-project-modal";
import { useIsCompactSidebar } from "hooks/use-mobile";

export interface ConversationActionsProps {
    conversationId: string;
    conversationTitle: string;
    projectId: string;
    projectName: string;
    invalidateProject?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export const ConversationActions = ({ conversationId, conversationTitle, projectId, projectName, invalidateProject = false, onOpenChange }: ConversationActionsProps) => {

    const activeConversationId = useChatStore((state) => state.activeConversationId);
    const { t } = useTranslation();
    const { toast } = useToast();
    const { configurationSettings } = useConfig();
    const { mutateAsync: updateProject } = useUpdateProject();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const queryClient = useQueryClient();
    const isActive = pathname.startsWith(`/chat/${conversationId}`);
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const updateConversationTitleInStore = useChatStore((s) => s.updateConversationTitleInStore);
    const removeConversationFromSidebarProjectListById = useChatStore((s) => s.removeConversationFromSidebarProjectListById);
    const appendToSidebarConversationsList = useChatStore((state) => state.appendToSidebarConversationsList);
    const fetchAndUpdateProjectAllConversations = useChatStore((state) => state.fetchAndUpdateProjectAllConversations);
    const sidebarProjectsList = useChatStore((s) => s.sidebarProjectsList);

    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRenameLoading, setIsRenameLoading] = useState(false);
    const [newTitle, setNewTitle] = useState(conversationTitle);
    const [actionType, setActionType] = useState<"delete" | "archive" | "rename" | "remove" | null>(null);
    const [open, setOpen] = useState(false);
    const [isProjectsOpen, setIsProjectsOpen] = useState(false);
    const isCompact = useIsCompactSidebar();

    const createSidebarItemFromProjectConversation = (conversationId: string, projectId: string): SidebarItem | undefined => {
        const currentProject = sidebarProjectsList.find((p) => p.id === projectId);
        if (!currentProject) return;
        const conversation = currentProject.items?.find((i) => i.ConversationId === conversationId);
        if (!conversation) return;
        return {
            ConversationId: conversation.ConversationId,
            ConversationTitle: conversation.ConversationTitle,
            CreatedBy: conversation.CreatedBy,
            TenantId: conversation.TenantId,
            CreatedDate: conversation.CreatedDate,
            LastUpdatedDate: conversation.LastUpdatedDate,
            Conversations: [],
            IsGroup: false,
        };
    };

    const handleRename = async () => {
        if (!newTitle.trim()) return;
        try {
            setIsRenameLoading(true);
            await updateProject({ payload: { conversationId, conversationTitle: newTitle } });
            if (invalidateProject) {
                queryClient.invalidateQueries({ queryKey: ["getProject", projectId] });
            }
            updateConversationTitleInStore(conversationId, newTitle, { isManual: true });
            toast({ variant: "success", title: t("SUCCESS"), description: t("RENAME_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("RENAME_FAILED"), description: t("RENAME_FAILED_DESCRIPTION") });
        } finally {
            setIsRenameDialogOpen(false);
            setIsRenameLoading(false);
        }
    };

    const handleArchive = async () => {
        try {
            setIsLoading(true);
            // await archiveConversationById(conversationId, configurationSettings);
            // const payload = createPayloadForUpdateProjectConversation(conversationId, projectId);
            // if (payload) await updateProject({ payload, skipInvalidate: true });
            await updateProject({ payload: { isArchived: true, conversationId } });
            if (invalidateProject) {
                queryClient.invalidateQueries({ queryKey: ["getProject", projectId] });
            }
            removeConversationFromSidebarProjectListById(conversationId);
            if (!invalidateProject) {
                if (activeConversationId === conversationId) {
                    useChatStore.getState().setNewChat(true);
                    navigate("/chat");
                }
            }
            toast({ variant: "success", title: t("SUCCESS"), description: t("ARCHIVE_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("ARCHIVE_FAILED"), description: t("ARCHIVE_FAILED_DESCRIPTION") });
        } finally {
            setIsConfirmDialogOpen(false);
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setIsLoading(true);
            markConversationDeletedLocally(conversationId);
            await deleteConversationById(conversationId, configurationSettings);
            if (invalidateProject) {
                queryClient.invalidateQueries({ queryKey: ["getProject", projectId] });
            }
            removeConversationFromSidebarProjectListById(conversationId);
            if (!invalidateProject) {
                useChatStore.getState().setNewChat(true);
                navigate("/chat");
            }
            toast({ variant: "success", title: t("SUCCESS"), description: t("DELETE_SUCCESS_DESCRIPTION") });
        } catch {
            clearLocallyDeletedConversation(conversationId);
            toast({ variant: "destructive", title: t("DELETE_FAILED"), description: t("DELETE_FAILED_DESCRIPTION") });
        } finally {
            setIsConfirmDialogOpen(false);
            setIsLoading(false);
        }
    };

    const handleRemove = async () => {
        try {
            setIsLoading(true);
            // const payload = createPayloadForUpdateProjectConversation(conversationId, projectId);
            await updateProject({ payload: { conversationId, projectId: "" } });
            if (invalidateProject) {
                queryClient.invalidateQueries({ queryKey: ["getProject", projectId] });
            }
            const sidebarItem = createSidebarItemFromProjectConversation(conversationId, projectId);
            if (sidebarItem) appendToSidebarConversationsList(sidebarItem);
            removeConversationFromSidebarProjectListById(conversationId);
            toast({ variant: "success", title: t("SUCCESS"), description: t("REMOVE_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("REMOVE_FAILED"), description: t("REMOVE_FAILED_DESCRIPTION") });
        } finally {
            setIsConfirmDialogOpen(false);
            setIsLoading(false);
        }
    };

    const handleMoveToProject = async (toProjectId: string, fromProjectId: string) => {
        try {
            const toProject = sidebarProjectsList.find((p) => p.id === toProjectId);
            if (!toProject) return;
            const fromProject = sidebarProjectsList.find((p) => p.id === fromProjectId);
            if (!fromProject) return;

            await updateProject({ payload: { conversationId, projectId: toProjectId } });

            await Promise.all([
                fetchAndUpdateProjectAllConversations(configurationSettings, toProjectId),
                fetchAndUpdateProjectAllConversations(configurationSettings, fromProjectId)
            ]);
            toast({ variant: "success", title: t("SUCCESS"), description: t("MOVE_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("MOVE_FAILED"), description: t("MOVE_FAILED_DESCRIPTION") });
        } finally {
            setIsConfirmDialogOpen(false);
            setIsLoading(false);
        }
    };

    const handleConfirmAction = () => {
        if (actionType === "rename") handleRename();
        if (actionType === "archive") handleArchive();
        if (actionType === "remove") handleRemove();
        if (actionType === "delete") handleDelete();
    };

    return (
        <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <DropdownMenu modal={false} open={open} onOpenChange={(current) => {
                setOpen(current);
                if (!current) setIsProjectsOpen(false);
                onOpenChange?.(current);
            }}>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'ml-1 rounded-md p-1 transition-opacity hover:bg-muted-foreground/10',
                            open
                                ? 'opacity-100'
                                : 'opacity-100 lg:pointer-events-none lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100',
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MoreHoriz
                            width={16}
                            height={16}
                            strokeWidth={1.5}
                            className={cn(
                                isActive || isHovered ? 'text-[#3D3D3D] dark:text-white' : 'text-high-emphasis group-hover:text-[#3D3D3D] dark:group-hover:!text-[#fff]',
                            )}
                        />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="bottom" align="start">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setIsRenameDialogOpen(true); setNewTitle(conversationTitle); setActionType("rename"); }}>
                        <ConversationMenuIcon optionId="rename" />
                        <span>{t('RENAME')}</span>
                    </DropdownMenuItem>
                    {isCompact ? (
                        <>
                            <DropdownMenuItem
                                onSelect={(event) => {
                                    event.preventDefault();
                                    setIsProjectsOpen((current) => !current);
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ConversationMenuIcon optionId="add-to-project" />
                                <span>{t("MOVE_TO_PROJECT")}</span>
                                <NavArrowRight
                                    width={16}
                                    height={16}
                                    strokeWidth={1.5}
                                    className={cn('ml-auto size-4 shrink-0', isProjectsOpen && 'rotate-90')}
                                />
                            </DropdownMenuItem>
                            {isProjectsOpen && (
                                <>
                                    <DropdownMenuItem
                                        className="pl-8"
                                        onClick={(e) => { e.stopPropagation(); setIsAddProjectModalOpen(true); }}
                                    >
                                        <ConversationMenuIcon optionId="new-project" />
                                        <span>{t("NEW_PROJECT")}</span>
                                    </DropdownMenuItem>
                                    {sidebarProjectsList
                                        .filter(p => p.id !== projectId)
                                        .map(project => (
                                            <DropdownMenuItem
                                                key={project.id}
                                                className="pl-8"
                                                onClick={(e) => { e.stopPropagation(); handleMoveToProject(project.id, projectId); }}
                                            >
                                                <ConversationMenuIcon optionId={project.id} />
                                                <span>{project.name}</span>
                                            </DropdownMenuItem>
                                        ))}
                                </>
                            )}
                        </>
                    ) : (
                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()}>
                                <ConversationMenuIcon optionId="add-to-project" />
                                <span>{t("MOVE_TO_PROJECT")}</span>
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setIsAddProjectModalOpen(true); }}>
                                    <ConversationMenuIcon optionId="new-project" />
                                    <span>{t("NEW_PROJECT")}</span>
                                </DropdownMenuItem>
                                {sidebarProjectsList
                                    .filter(p => p.id !== projectId)
                                    .map(project => (
                                        <DropdownMenuItem
                                            key={project.id}
                                            onClick={(e) => { e.stopPropagation(); handleMoveToProject(project.id, projectId); }}
                                        >
                                            <ConversationMenuIcon optionId={project.id} />
                                            <span>{project.name}</span>
                                        </DropdownMenuItem>
                                    ))}
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>
                    )}

                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setIsConfirmDialogOpen(true); setActionType("remove"); }}>
                        <ConversationMenuIcon optionId="remove" />
                        <span>{t("REMOVE_FROM")}</span> {' '}
                        <span className="font-medium">{projectName}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setIsConfirmDialogOpen(true); setActionType("archive"); }}>
                        <ConversationMenuIcon optionId="archive" />
                        <span>{t("ARCHIVE")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        className={cn(dropdownMenuItemDestructiveClass)}
                        onClick={(e) => { e.stopPropagation(); setIsConfirmDialogOpen(true); setActionType("delete"); }}
                    >
                        <ConversationMenuIcon optionId="delete" />
                        <span>{t("DELETE")}</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <CustomDialog
                isOpen={isConfirmDialogOpen}
                isLoading={isLoading}
                setIsOpen={setIsConfirmDialogOpen}
                title={t("CONFIRM_ACTION")}
                onConfirm={handleConfirmAction}
                confirmText={t("YES")}
                cancelText={t("CANCEL")}
                description={""}
            >
                <p>{t("CONFIRM_ACTION_DESCRIPTION")}</p>
            </CustomDialog>

            <CustomDialog
                isOpen={isRenameDialogOpen}
                isLoading={isRenameLoading}
                setIsOpen={setIsRenameDialogOpen}
                title={t("RENAME")}
                onConfirm={handleConfirmAction}
                confirmText={t("SAVE")}
                cancelText={t("CANCEL")}
                isConfirmDisabled={newTitle.trim() === ""}
                description={""}
            >
                <DebouncedInput
                    placeholder={t("ENTER_NEW_TITLE")}
                    initialValue={newTitle}
                    onDebouncedChange={setNewTitle}
                    className="
                                        h-10 w-full rounded-3xl
                                        border border-primary
                                        bg-background pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    " />
            </CustomDialog>

            <NewProjectModal callback={async () => {
                await fetchAndUpdateProjectAllConversations(configurationSettings, projectId);
            }} newConversatioId={conversationId} isOpen={isAddProjectModalOpen} onClose={() => setIsAddProjectModalOpen(false)} />
        </div>
    );
};